/**
 * Utility functions for working with browser storage with fallbacks
 * Handles cases where localStorage might not be available:
 * - Private/incognito mode
 * - User has disabled storage
 * - Older browsers
 */

// Approximate max size for localStorage (5MB in bytes)
const MAX_STORAGE_SIZE = 5 * 1024 * 1024;

/**
 * Compress string data using simple LZW compression if it exceeds threshold
 */
const compressData = (data: string): string => {
    // Only compress if the data is larger than 50KB
    if (data.length < 50 * 1024) {
        return data;
    }

    try {
        // Use built-in compression in modern browsers if available
        if (typeof window !== 'undefined' && 'CompressionStream' in window) {
            return data; // Skip for now - requires async implementation
        }

        // Simple compression: if a string is too long, truncate it to a max length
        // This is a fallback if we can't use proper compression
        if (data.length > 1 * 1024 * 1024) { // 1MB
            console.warn('Data too large for localStorage, truncating');
            return data.substring(0, 1 * 1024 * 1024); // Truncate to 1MB
        }

        return data;
    } catch (e) {
        console.error('Error compressing data:', e);
        return data;
    }
};

/**
 * Decompress string data
 */
const decompressData = (data: string): string => {
    // We're just using truncation above, so no decompression needed in this basic implementation
    return data;
};

/**
 * Calculate approximate size of a string in localStorage
 */
const getApproximateSize = (str: string): number => {
    // Each character is approximately 2 bytes in JS
    return str.length * 2;
};

/**
 * Tests if localStorage is available
 */
export const isLocalStorageAvailable = (): boolean => {
    try {
        const testKey = '__storage_test__';
        localStorage.setItem(testKey, testKey);
        localStorage.removeItem(testKey);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Tests if sessionStorage is available
 */
export const isSessionStorageAvailable = (): boolean => {
    try {
        const testKey = '__storage_test__';
        sessionStorage.setItem(testKey, testKey);
        sessionStorage.removeItem(testKey);
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Get the current usage of localStorage in bytes (approximately)
 */
export const getLocalStorageUsage = (): number => {
    if (!isLocalStorageAvailable()) return 0;

    let total = 0;
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
            const value = localStorage.getItem(key) || '';
            total += getApproximateSize(key) + getApproximateSize(value);
        }
    }
    return total;
};

/**
 * Clean up old storage items by prefix
 */
export const cleanupStorageByPrefix = (prefix: string): void => {
    if (!isLocalStorageAvailable()) return;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
};

// Fallback in-memory storage
const memoryStorage: Record<string, string> = {};

/**
 * Saves state to the best available storage option
 * Falls back gracefully between:
 * 1. localStorage (persistent)
 * 2. sessionStorage (session-based)
 * 3. In-memory JavaScript object (page refresh will lose data)
 */
export const saveStateToStorage = (key: string, data: any): void => {
    try {
        // Serialize the data to a string
        const serializedData = JSON.stringify(data);

        // Get the approximate size of the data
        const dataSize = getApproximateSize(serializedData);


        // Check if we're close to the localStorage limit
        if (isLocalStorageAvailable()) {
            const currentUsage = getLocalStorageUsage();
            const availableSpace = MAX_STORAGE_SIZE - currentUsage;

            if (dataSize > availableSpace) {
                console.warn(`[Storage] Not enough space in localStorage. Available: ${availableSpace} bytes, Required: ${dataSize} bytes`);
                cleanupStorageByPrefix('appIdea_'); // Clean up old app ideas
            }
        }

        // Compress the data if necessary
        const compressedData = compressData(serializedData);

        // Try localStorage first
        if (isLocalStorageAvailable()) {
            localStorage.setItem(key, compressedData);
            return;
        }

        // Try sessionStorage next
        if (isSessionStorageAvailable()) {
            sessionStorage.setItem(key, compressedData);

            return;
        }

        // Last resort: in-memory storage
        memoryStorage[key] = compressedData;

    } catch (error) {
        console.error('Error saving state to storage:', error);

        // If we get a quota error, try to clean up and try again
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
            try {
                cleanupStorageByPrefix('appIdea_');

                // Try localStorage with possibly compressed data
                if (isLocalStorageAvailable()) {
                    const serializedData = JSON.stringify(data);
                    const compressedData = compressData(serializedData);
                    localStorage.setItem(key, compressedData);
                }
            } catch (retryError) {
                console.error('Error saving state to storage after cleanup:', retryError);
            }
        }
    }
};

/**
 * Gets state from the best available storage option
 * Checks all storage options in order
 */
export const getStateFromStorage = <T>(key: string): T | null => {
    try {
        let serializedData: string | null = null;

        // Try localStorage first
        if (isLocalStorageAvailable()) {
            serializedData = localStorage.getItem(key);
            if (serializedData) {
                const decompressedData = decompressData(serializedData);
                return JSON.parse(decompressedData);
            }
        }

        // Try sessionStorage next
        if (isSessionStorageAvailable()) {
            serializedData = sessionStorage.getItem(key);
            if (serializedData) {
                const decompressedData = decompressData(serializedData);
                return JSON.parse(decompressedData);
            }
        }

        // Last resort: in-memory storage
        if (memoryStorage[key]) {
            const decompressedData = decompressData(memoryStorage[key]);
            return JSON.parse(decompressedData);
        }

        return null;
    } catch (error) {
        console.error('Error retrieving state from storage:', error);
        return null;
    }
};

/**
 * Removes state from all storage options
 */
export const removeStateFromStorage = (key: string): void => {
    try {
        if (isLocalStorageAvailable()) {
            localStorage.removeItem(key);
        }

        if (isSessionStorageAvailable()) {
            sessionStorage.removeItem(key);
        }

        delete memoryStorage[key];
    } catch (error) {
        console.error('Error removing state from storage:', error);
    }
}; 